import { useQuery } from '@/apollo';
import { gql } from '@/apollo';
import { GET_APPOINTMENT_DETAILS } from '../../../graphql/appointment-details';
import { GET_CUSTOMERS } from '../../../graphql/customers';
import type { 
  Insurance, 
  Location, 
  Condition, 
  Provider, 
  Customer, 
  AppointmentDetail, 
  State,
  Language
} from '../types/moderator.types';

// GraphQL Queries
export const GET_MODERATOR_DATA = gql`
  query GetModeratorData {
    moderatorData {
      insurances {
        id
        name
        description
        isActive
        customerId
        customer {
          id
          name
        }
      }
      locations {
        id
        name
        city
        stateId
        state {
          id
          code
          name
        }
        customerId
        customer {
          id
          name
        }
        zipCode
        physicalAddress
        notes {
          id
          content
          order
        }
      }
      conditions {
        id
        name
        categoryId
        category {
          id
          name
        }
        description
        canCancel
        canReschedule
        notes {
          id
          content
          order
        }
      }
      providers {
        id
        firstName
        lastName
        title
        customerId
        customer {
          id
          name
        }
        languages {
          id
          languageId
          language {
            id
            code
            name
          }
        }
        phoneNumbers {
          id
          name
          phone
          location {
            id
            name
            state {
              id
              code
              name
            }
            physicalAddress
          }
        }
        maLvnName
        maLvnPhone
        triageNurseName
        triageNursePhone
        surgerySchedulerName
        surgerySchedulerPhone
        practiceAdminName
        practiceAdminPhone
        minimumAge
        maximumAge
        ageRestrictionNotes
        conditions {
          id
          condition {
            id
            name
          }
        }
        locations {
          id
          location {
            id
            name
            state {
              id
              code
              name
            }
            physicalAddress
          }
          address
          suite
          phone
        }
        insurances {
          id
          insurance {
            id
            name
          }
          notes
        }
        isActive
        acceptsNewPatients
        requiresReferral
        schedulingInstructions
        insuranceNotes
        supervisingProviderId
        supervisingProvider {
          id
          firstName
          lastName
          title
        }
        supervisingProviderIds
        supervisingProviders {
          id
          firstName
          lastName
          title
        }
        apps {
          id
          firstName
          lastName
          title
        }
        notes {
          id
          content
          category
          order
          createdAt
          updatedAt
          createdBy
        }
      }
      states {
        id
        code
        name
        isActive
      }
      languages {
        id
        code
        name
        isActive
      }
    }
  }
`;

export const GET_STATES = gql`
  query GetStates {
    states {
      id
      code
      name
      isActive
    }
  }
`;

export const GET_LOCATIONS = gql`
  query GetLocations {
    locations {
      id
      name
      city
      state {
        id
        code
        name
      }
      zipCode
      physicalAddress
      isActive
    }
  }
`;

export const GET_CONDITIONS = gql`
  query GetConditions {
    conditions {
      id
      name
      category
      description
      canCancel
      canReschedule
      isActive
      createdAt
      updatedAt
      notes {
        id
        content
        order
      }
    }
  }
`;

export const GET_INSURANCES = gql`
  query GetInsurances {
    insurances {
      id
      name
      description
      isActive
      createdAt
      updatedAt
    }
  }
`;

export interface UseModeratorDataReturn {
  insurances: Insurance[];
  locations: Location[];
  conditions: Condition[];
  providers: Provider[];
  customers: Customer[];
  appointmentDetails: AppointmentDetail[];
  states: State[];
  languages: Language[];
  loading: boolean;
  error: any;
  refetch: () => void;
  refetchInsurances: () => void;
  refetchLocations: () => void;
  refetchConditions: () => void;
  refetchCustomers: () => void;
  refetchAppointmentDetails: () => void;
  refetchModeratorData: () => void;
}

export const useModeratorData = (): UseModeratorDataReturn => {
  // Query states separately for fallback
  const { data: statesData } = useQuery(GET_STATES, { fetchPolicy: 'network-only' });
  
  // Query locations separately for fallback
  const { data: locationsData, refetch: refetchLocations } = useQuery(GET_LOCATIONS, { 
    fetchPolicy: 'network-only' 
  });
  
  // Query conditions separately
  const { data: conditionsData, refetch: refetchConditions } = useQuery(GET_CONDITIONS, { 
    fetchPolicy: 'network-only' 
  });
  
  // Query insurances separately
  const { data: insurancesData, refetch: refetchInsurances } = useQuery(GET_INSURANCES, { 
    fetchPolicy: 'network-only' 
  });
  
  // Query customers
  const { data: customersData, refetch: refetchCustomers } = useQuery(GET_CUSTOMERS, { 
    fetchPolicy: 'network-only' 
  });
  
  // Query appointment details
  const { data: appointmentDetailsData, refetch: refetchAppointmentDetails } = useQuery(
    GET_APPOINTMENT_DETAILS, 
    { fetchPolicy: 'network-only' }
  );
  
  // Main moderator data query
  const { data: moderatorData, loading, error, refetch: refetchModeratorData } = useQuery(
    GET_MODERATOR_DATA, 
    { 
      skip: false,
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true
    }
  );

  // Combine all data
  const insurances: Insurance[] = moderatorData?.moderatorData?.insurances || insurancesData?.insurances || [];
  const locations: Location[] = moderatorData?.moderatorData?.locations || locationsData?.locations || [];
  const conditions: Condition[] = moderatorData?.moderatorData?.conditions || conditionsData?.conditions || [];
  const providers: Provider[] = moderatorData?.moderatorData?.providers || [];
  const customers: Customer[] = customersData?.customers || [];
  const appointmentDetails: AppointmentDetail[] = appointmentDetailsData?.appointmentDetails || [];
  const states: State[] = moderatorData?.moderatorData?.states || statesData?.states || [];
  const languages: Language[] = moderatorData?.moderatorData?.languages || [];

  const refetch = () => {
    refetchModeratorData();
    refetchInsurances();
    refetchLocations();
    refetchConditions();
    refetchCustomers();
    refetchAppointmentDetails();
  };

  return {
    insurances,
    locations,
    conditions,
    providers,
    customers,
    appointmentDetails,
    states,
    languages,
    loading,
    error,
    refetch,
    refetchInsurances,
    refetchLocations,
    refetchConditions,
    refetchCustomers,
    refetchAppointmentDetails,
    refetchModeratorData,
  };
};

